import {
	BreakdownRequest,
	BreakdownResult,
	ChurnInsightsResult,
	CustomerTimeline,
	JourneyCustomer,
	JourneyCustomersPage,
	JourneyCustomersRequest,
	JourneyExploreResult,
	JourneyExportRequest,
	JourneyFilterState,
	JourneyFlowResult,
	JourneyInteraction,
	JourneyMeta,
	JourneyPath,
	JourneySegment,
	JourneySimplify,
	JourneyTransition,
	SavedView,
	SavedViewState,
	TimeBetweenRequest,
	TimeBetweenResult,
} from '../../../models/journey-explorer/journey-explorer';
import { IJourneyExplorerApiService } from '../../../api/services/journey-explorer-api-service';

// Made-up subscription customers for demoing the Website / Subscription switch before the real
// subscription data exists. A fixed seed keeps every load identical.

const SEED = 20261103;
const CUSTOMER_COUNT = 900;
const DATA_MIN = '2025-01-01';
const DATA_MAX = '2026-10-31';
const DAY_SECS = 86400;
const MAX_STEPS = 14;
const MAX_PATH_STEPS = 15;
const MAX_PATHS = 50;
const DETAIL_TABLE = 'JOURNEY_DIM_INTERACTION';
const NOT_SET = 'Not set';
const PATH_SEPARATOR = ' > ';
export const DEMO_UNAVAILABLE = 'This part is not available in the Subscription demo yet.';

const TYPES = [
	{
		type: 'subscription',
		label: 'Subscription',
		color: '#b08638',
		interactions: [
			'Sign Up',
			'First Order',
			'Order Delivered',
			'Skip Box',
			'Pause',
			'Resume',
			'Plan Change',
			'Cancel',
		],
	},
	{ type: 'support', label: 'Support', color: '#9d6b8c', interactions: ['Support Ticket', 'Complaint'] },
	{ type: 'marketing', label: 'Marketing', color: '#3f8f97', interactions: ['Email Open', 'Promo Redeemed'] },
	{ type: 'survey', label: 'Survey', color: '#7b6fb5', interactions: ['NPS Survey'] },
];
const CANCEL = 'Cancel';
const TYPE_OF = new Map(TYPES.flatMap((type) => type.interactions.map((name): [string, string] => [name, type.type])));

interface Detail {
	column: string;
	label: string;
	values: string[];
}

const SUPPORT_CHANNEL: Detail = {
	column: 'SUPPORT_CHANNEL',
	label: 'Support channel',
	values: ['Email', 'Phone', 'Chat'],
};
const DETAILS: Record<string, Detail[]> = {
	'Order Delivered': [{ column: 'BOX_SIZE', label: 'Box size', values: ['Small', 'Medium', 'Large'] }],
	'Skip Box': [{ column: 'SKIP_REASON', label: 'Skip reason', values: ['Away', 'Too much food', 'Budget'] }],
	'Support Ticket': [SUPPORT_CHANNEL],
	Complaint: [SUPPORT_CHANNEL],
	Cancel: [
		{
			column: 'CANCEL_REASON',
			label: 'Cancel reason',
			values: ['Too expensive', 'Too much food', 'Delivery issues', 'Moving'],
		},
	],
	'NPS Survey': [{ column: 'NPS_GROUP', label: 'NPS group', values: ['Promoter', 'Passive', 'Detractor'] }],
};

const CUSTOMER_ATTRIBUTES = [
	{ column: 'PLAN', label: 'Plan', values: ['Monthly', 'Quarterly', 'Annual'], weights: [5, 2, 3] },
	{ column: 'COUNTRY', label: 'Country', values: ['UK', 'Ireland'], weights: [85, 15] },
	{
		column: 'ACQUISITION_CHANNEL',
		label: 'Acquisition channel',
		values: ['Paid Social', 'Organic', 'Referral', 'Partner'],
		weights: [35, 35, 20, 10],
	},
];

const NEXT: Record<string, [string, number][]> = {
	'Sign Up': [
		['First Order', 0.9],
		['Email Open', 0.1],
	],
	'First Order': [['Order Delivered', 1]],
	'Order Delivered': [
		['Order Delivered', 0.35],
		['Skip Box', 0.2],
		['Email Open', 0.12],
		['Support Ticket', 0.1],
		['NPS Survey', 0.08],
		['Plan Change', 0.07],
		['Cancel', 0.05],
	],
	'Skip Box': [
		['Order Delivered', 0.45],
		['Pause', 0.2],
		['Cancel', 0.2],
		['Skip Box', 0.15],
	],
	Pause: [
		['Resume', 0.55],
		['Cancel', 0.45],
	],
	Resume: [['Order Delivered', 1]],
	'Support Ticket': [
		['Order Delivered', 0.6],
		['Complaint', 0.25],
		['Cancel', 0.15],
	],
	Complaint: [
		['Order Delivered', 0.5],
		['Cancel', 0.5],
	],
	'Email Open': [
		['Order Delivered', 0.7],
		['Promo Redeemed', 0.3],
	],
	'Promo Redeemed': [['Order Delivered', 1]],
	'Plan Change': [['Order Delivered', 1]],
	'NPS Survey': [
		['Order Delivered', 0.8],
		['Cancel', 0.2],
	],
};

const GAP_DAYS: Record<string, [number, number]> = {
	'Sign Up': [0, 2],
	'First Order': [2, 5],
	'Order Delivered': [5, 9],
	Pause: [14, 40],
};

interface DemoStep {
	name: string;
	type: string;
	ts: number;
	details: Record<string, string>;
}

interface DemoCustomer {
	id: string;
	start: string;
	startTs: number;
	attributes: Record<string, string>;
	steps: DemoStep[];
}

const toTs = (date: string) => Date.parse(`${date}T00:00:00Z`) / 1000;
const toDate = (ts: number) => new Date(ts * 1000).toISOString().slice(0, 10);
const toIso = (ts: number) => new Date(ts * 1000).toISOString();

const mulberry32 = (seed: number) => {
	let state = seed;
	return () => {
		state = (state + 0x6d2b79f5) | 0;
		let t = Math.imul(state ^ (state >>> 15), 1 | state);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
};

const generate = (): DemoCustomer[] => {
	const random = mulberry32(SEED);
	const pick = <T>(items: T[], weights: number[]): T => {
		const total = weights.reduce((sum, weight) => sum + weight, 0);
		let roll = random() * total;
		for (let index = 0; index < items.length; index++) {
			roll -= weights[index];
			if (roll <= 0) return items[index];
		}
		return items[items.length - 1];
	};
	const minTs = toTs(DATA_MIN);
	const maxTs = toTs(DATA_MAX) + DAY_SECS - 1;
	const latestStart = maxTs - 30 * DAY_SECS;

	return Array.from({ length: CUSTOMER_COUNT }, (_, index) => {
		const startTs = toTs(toDate(minTs + Math.floor(random() * (latestStart - minTs))));
		const attributes = Object.fromEntries(
			CUSTOMER_ATTRIBUTES.map((attribute): [string, string] => [
				attribute.column,
				pick(attribute.values, attribute.weights),
			])
		);
		let cancelFactor = { Monthly: 1.4, Quarterly: 1, Annual: 0.5 }[attributes.PLAN] ?? 1;
		if (attributes.ACQUISITION_CHANNEL === 'Paid Social') cancelFactor *= 1.3;
		if (attributes.ACQUISITION_CHANNEL === 'Referral') cancelFactor *= 0.7;

		const steps: DemoStep[] = [];
		let name = 'Sign Up';
		let ts = startTs + Math.floor(random() * 6 * 3600);
		while (steps.length < MAX_STEPS && ts <= maxTs) {
			const details = Object.fromEntries(
				(DETAILS[name] ?? []).map((detail): [string, string] => [
					detail.column,
					detail.values[Math.floor(random() * detail.values.length)],
				])
			);
			steps.push({ name, type: TYPE_OF.get(name) ?? 'subscription', ts, details });
			if (name === CANCEL || (name === 'Order Delivered' && random() < 0.1)) break;
			const options = NEXT[name];
			name = pick(
				options.map(([next]) => next),
				options.map(([next, weight]) => (next === CANCEL ? weight * cancelFactor : weight))
			);
			const [low, high] = GAP_DAYS[steps[steps.length - 1].name] ?? [0.5, 6];
			ts += Math.floor((low + random() * (high - low)) * DAY_SECS);
		}
		return {
			id: `SUB-${100001 + index}`,
			start: toDate(startTs),
			startTs,
			attributes,
			steps,
		};
	});
};

let dataset: DemoCustomer[] | null = null;
const customers = () => {
	dataset = dataset ?? generate();
	return dataset;
};

export const SUBSCRIPTION_DEMO_META: JourneyMeta = {
	data_range: { min: DATA_MIN, max: DATA_MAX },
	data_version: 'subscription-demo',
	currency: 'GBP',
	customer_dimensions: [...CUSTOMER_ATTRIBUTES.map(({ column, label, values }) => ({ column, label, values }))],
	interaction_types: TYPES.map((type) => {
		const details = new Map<string, Detail>();
		type.interactions.forEach((name) =>
			(DETAILS[name] ?? []).forEach((detail) => details.set(detail.column, detail))
		);
		return {
			type: type.type,
			label: type.label,
			color: type.color,
			interactions: type.interactions.map((name) => ({
				name,
				label: name,
				x: null,
				y: null,
			})),
			dimensions: Array.from(details.values()).map((detail) => ({
				table: DETAIL_TABLE,
				column: detail.column,
				label: detail.label,
				values: detail.values,
			})),
		};
	}),
};

interface ViewStep {
	name: string;
	type: string;
	ts: number;
}

interface Selected {
	customer: DemoCustomer;
	steps: DemoStep[];
	view: ViewStep[];
	firstGoalTs: number | null;
	lastGoalTs: number | null;
	secsToGoal: number | null;
}

const reachedAfter = (item: Selected, ts: number | null) =>
	item.lastGoalTs !== null && (ts === null || item.lastGoalTs >= ts);

const hasPair = (steps: { name: string }[], from: string, to: string) =>
	steps.some((step, index) => index > 0 && steps[index - 1].name === from && step.name === to);

const simplifySteps = (steps: DemoStep[], simplify?: JourneySimplify): ViewStep[] => {
	const hidden = new Set(simplify?.hidden ?? []);
	const groupOf = new Map(
		(simplify?.groups ?? []).flatMap((group) => group.names.map((name): [string, string] => [name, group.label]))
	);
	return steps
		.filter((step) => !hidden.has(step.name))
		.map((step) => ({ name: groupOf.get(step.name) ?? step.name, type: step.type, ts: step.ts }));
};

const select = (filters: JourneyFilterState, applyFilters: boolean): Selected[] => {
	const fromTs = toTs(filters.interaction_range.from);
	const endTs = toTs(filters.interaction_range.to) + DAY_SECS;
	const goal = filters.goal?.interaction ?? null;
	const outcome = filters.goal?.outcome ?? null;

	return customers().flatMap((customer): Selected[] => {
		if (customer.start < filters.customer_start.from || customer.start > filters.customer_start.to) return [];
		const steps = customer.steps.filter((step) => step.ts >= fromTs && step.ts < endTs);
		if (steps.length === 0) return [];
		const goalSteps = goal ? steps.filter((step) => step.name === goal) : [];
		const firstGoalTs = goalSteps.length ? goalSteps[0].ts : null;
		const lastGoalTs = goalSteps.length ? goalSteps[goalSteps.length - 1].ts : null;

		if (applyFilters) {
			if (outcome === 'reached' && firstGoalTs === null) return [];
			if (outcome === 'not_reached' && firstGoalTs !== null) return [];
			const customerMatch = filters.customer_filters.every(
				(filter) =>
					filter.values.length === 0 || filter.values.includes(customer.attributes[filter.column] ?? NOT_SET)
			);
			const detailMatch = filters.dimension_filters.every(
				(filter) =>
					filter.values.length === 0 ||
					steps.some((step) => filter.values.includes(step.details[filter.column] ?? NOT_SET))
			);
			const interactionMatch = filters.interaction_filters.every((filter) => {
				const had = steps.some((step) => step.name === filter.interaction_name);
				if (filter.mode === 'passed_through') return had;
				if (filter.mode === 'did_not_reach') return !had;
				if (filter.mode === 'started_here') return steps[0].name === filter.interaction_name;
				return steps[steps.length - 1].name === filter.interaction_name;
			});
			const transitionMatch = filters.transition_filters.every((filter) =>
				filter.mode === 'went_through'
					? hasPair(steps, filter.from, filter.to)
					: !hasPair(steps, filter.from, filter.to)
			);
			if (!customerMatch || !detailMatch || !interactionMatch || !transitionMatch) return [];
		}

		return [
			{
				customer,
				steps,
				view: simplifySteps(steps, filters.simplify),
				firstGoalTs,
				lastGoalTs,
				secsToGoal: firstGoalTs === null ? null : firstGoalTs - steps[0].ts,
			},
		];
	});
};

const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);

const quantile = (values: number[], q: number): number | null => {
	if (values.length === 0) return null;
	const sorted = [...values].sort((a, b) => a - b);
	const position = (sorted.length - 1) * q;
	const lower = Math.floor(position);
	const upper = Math.ceil(position);
	return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
};
const median = (values: number[]) => quantile(values, 0.5);
const average = (values: number[]) =>
	values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;

const pathKey = (path: string) => {
	let hash = 5381;
	for (let index = 0; index < path.length; index++) hash = (Math.imul(hash, 33) + path.charCodeAt(index)) >>> 0;
	return hash.toString(16).padStart(8, '0');
};
const pathOf = (item: Selected) =>
	item.view
		.slice(0, MAX_PATH_STEPS)
		.map((step) => step.name)
		.join(PATH_SEPARATOR);

interface Tally {
	customers: Set<string>;
	reached: Set<string>;
	count: number;
	gaps: number[];
}
const tally = (): Tally => ({ customers: new Set(), reached: new Set(), count: 0, gaps: [] });

const explore = (filters: JourneyFilterState): JourneyExploreResult => {
	const selected = select(filters, true);
	const all = select(filters, false);
	const interactions = new Map<string, Tally & { type: string; exits: number }>();
	const transitions = new Map<string, Tally & { from: string; to: string }>();
	const paths = new Map<string, { customers: number; reached: number; secs: number[] }>();

	selected.forEach((item) => {
		const id = item.customer.id;
		item.view.forEach((step, index) => {
			const next = item.view[index + 1];
			const stat = interactions.get(step.name) ?? { ...tally(), type: step.type, exits: 0 };
			stat.customers.add(id);
			if (reachedAfter(item, step.ts)) stat.reached.add(id);
			stat.count += 1;
			if (!next) stat.exits += 1;
			if (next) stat.gaps.push(next.ts - step.ts);
			interactions.set(step.name, stat);

			if (next) {
				const key = `${step.name}\u0000${next.name}`;
				const move = transitions.get(key) ?? { ...tally(), from: step.name, to: next.name };
				move.customers.add(id);
				if (reachedAfter(item, next.ts)) move.reached.add(id);
				move.count += 1;
				move.gaps.push(next.ts - step.ts);
				transitions.set(key, move);
			}
		});
		const path = pathOf(item);
		const entry = paths.get(path) ?? { customers: 0, reached: 0, secs: [] };
		entry.customers += 1;
		if (item.firstGoalTs !== null) entry.reached += 1;
		if (item.secsToGoal !== null) entry.secs.push(item.secsToGoal);
		paths.set(path, entry);
	});

	const reachedCount = selected.filter((item) => item.firstGoalTs !== null).length;
	const allReached = all.filter((item) => item.firstGoalTs !== null).length;
	const interactionCount = selected.reduce((sum, item) => sum + item.steps.length, 0);
	const secsToGoal = (items: Selected[]) =>
		items.flatMap((item) => (item.secsToGoal === null ? [] : [item.secsToGoal]));

	return {
		kpis: {
			customers: selected.length,
			customers_unfiltered: all.length,
			reached_customers: reachedCount,
			goal_rate_pct: pct(reachedCount, selected.length),
			goal_rate_pct_unfiltered: pct(allReached, all.length),
			median_secs_to_goal: median(secsToGoal(selected)),
			median_secs_to_goal_unfiltered: median(secsToGoal(all)),
			interactions: interactionCount,
			interactions_unfiltered: all.reduce((sum, item) => sum + item.steps.length, 0),
			avg_journey: selected.length ? Math.round((interactionCount / selected.length) * 10) / 10 : 0,
		},
		interactions: Array.from(interactions.entries()).map(
			([name, stat]): JourneyInteraction => ({
				name,
				type: stat.type,
				customers: stat.customers.size,
				reached: stat.reached.size,
				reached_pct: pct(stat.reached.size, stat.customers.size),
				interactions: stat.count,
				avg_secs_to_next: average(stat.gaps),
				median_secs_to_next: median(stat.gaps),
				exits: stat.exits,
			})
		),
		transitions: Array.from(transitions.values()).map(
			(move): JourneyTransition => ({
				from: move.from,
				to: move.to,
				customers: move.customers.size,
				reached: move.reached.size,
				reached_pct: pct(move.reached.size, move.customers.size),
				transitions: move.count,
				avg_secs: average(move.gaps),
				median_secs: median(move.gaps),
			})
		),
		paths: Array.from(paths.entries())
			.sort(([pathA, a], [pathB, b]) => b.customers - a.customers || pathA.localeCompare(pathB))
			.slice(0, MAX_PATHS)
			.map(
				([path, entry]): JourneyPath => ({
					path_key: pathKey(path),
					steps: path.split(PATH_SEPARATOR),
					customers: entry.customers,
					reached_pct: pct(entry.reached, entry.customers),
					median_secs_to_goal: median(entry.secs),
				})
			),
		meta: { query_ms: 0, cached: false, transitions_truncated: false },
	};
};

const members = (filters: JourneyFilterState, segment?: JourneySegment): Selected[] => {
	const selected = select(filters, true);
	if (!segment || segment.kind === 'flow') return selected;
	if (segment.kind === 'interaction')
		return selected.filter((item) => item.view.some((step) => step.name === segment.interaction));
	if (segment.kind === 'transition') return selected.filter((item) => hasPair(item.view, segment.from, segment.to));
	return selected.filter((item) => pathKey(pathOf(item)) === segment.path_key);
};

// When the customer joined the segment, so the goal only counts if it came after.
const memberTs = (item: Selected, segment: JourneySegment): number | null => {
	if (segment.kind === 'interaction') {
		return item.view.find((step) => step.name === segment.interaction)?.ts ?? null;
	}
	if (segment.kind === 'transition') {
		return (
			item.view.find(
				(step, index) => index > 0 && item.view[index - 1].name === segment.from && step.name === segment.to
			)?.ts ?? null
		);
	}
	return null;
};

const labelledAttributes = (customer: DemoCustomer) =>
	Object.fromEntries(
		CUSTOMER_ATTRIBUTES.map((attribute): [string, string | null] => [
			attribute.label,
			customer.attributes[attribute.column] ?? null,
		])
	);

const toCustomer = (item: Selected): JourneyCustomer => ({
	customer_id: item.customer.id,
	start_date: item.customer.start,
	attributes: labelledAttributes(item.customer),
	last_seen: toDate(item.customer.steps[item.customer.steps.length - 1].ts),
	goal_date: item.firstGoalTs === null ? null : toDate(item.firstGoalTs),
	journey: item.view.map((step) => step.name),
});

const segmentOf = (request: { segment?: JourneySegment; path_key?: string }): JourneySegment | undefined =>
	request.segment ?? (request.path_key ? { kind: 'path', path_key: request.path_key } : undefined);

const gapFor = (item: Selected, segment: JourneySegment): number | null => {
	const view = item.view;
	if (segment.kind === 'interaction') {
		const index = view.findIndex((step) => step.name === segment.interaction);
		return index >= 0 && index + 1 < view.length ? view[index + 1].ts - view[index].ts : null;
	}
	if (segment.kind === 'transition') {
		const index = view.findIndex(
			(step, position) => position > 0 && view[position - 1].name === segment.from && step.name === segment.to
		);
		return index > 0 ? view[index].ts - view[index - 1].ts : null;
	}
	return view.length > 1 ? view[view.length - 1].ts - view[0].ts : null;
};

const TIME_BINS: [string, number][] = [
	['Under 1 day', DAY_SECS],
	['1–3 days', 3 * DAY_SECS],
	['3–7 days', 7 * DAY_SECS],
	['1–2 weeks', 14 * DAY_SECS],
	['2–4 weeks', 28 * DAY_SECS],
	['Over 4 weeks', Number.POSITIVE_INFINITY],
];

const csvCell = (value: string | number | null) => {
	const text = value === null ? '' : String(value);
	return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export class SubscriptionDemoApiService implements IJourneyExplorerApiService {
	constructor(private readonly real: () => IJourneyExplorerApiService) {}

	public async getMeta(): Promise<JourneyMeta> {
		return SUBSCRIPTION_DEMO_META;
	}

	public async refresh(): Promise<void> {
		return undefined;
	}

	public async explore(filters: JourneyFilterState): Promise<JourneyExploreResult> {
		return explore(filters);
	}

	public async getCustomers(request: JourneyCustomersRequest): Promise<JourneyCustomersPage> {
		const list = members(request.filters, segmentOf(request)).sort((a, b) =>
			a.customer.id.localeCompare(b.customer.id)
		);
		const start = (request.page - 1) * request.page_size;
		return {
			total: list.length,
			page: request.page,
			page_size: request.page_size,
			customers: list.slice(start, start + request.page_size).map(toCustomer),
		};
	}

	public async getFlow(): Promise<JourneyFlowResult> {
		throw new Error(DEMO_UNAVAILABLE);
	}

	public async getChurnInsights(): Promise<ChurnInsightsResult> {
		throw new Error(DEMO_UNAVAILABLE);
	}

	public async getBreakdown(request: BreakdownRequest): Promise<BreakdownResult> {
		const list = members(request.filters, request.segment);
		const { table, column } = request.attribute;
		const valueOf = (item: Selected): string => {
			if (!table) {
				return item.customer.attributes[column] ?? NOT_SET;
			}
			const own = request.segment.kind === 'interaction' ? request.segment.interaction : null;
			const step =
				item.steps.find((candidate) => (own === null || candidate.name === own) && candidate.details[column]) ??
				item.steps.find((candidate) => candidate.details[column]);
			return step?.details[column] ?? NOT_SET;
		};
		const isReached = (item: Selected) => reachedAfter(item, memberTs(item, request.segment));
		const rows = new Map<string, { customers: number; reached: number }>();
		list.forEach((item) => {
			const value = valueOf(item);
			const row = rows.get(value) ?? { customers: 0, reached: 0 };
			row.customers += 1;
			if (isReached(item)) row.reached += 1;
			rows.set(value, row);
		});
		const reached = list.filter(isReached).length;
		return {
			customers: list.length,
			reached,
			reached_pct: pct(reached, list.length),
			truncated: false,
			rows: Array.from(rows.entries())
				.sort(([valueA, a], [valueB, b]) => b.customers - a.customers || valueA.localeCompare(valueB))
				.map(([value, row]) => ({ value, ...row, reached_pct: pct(row.reached, row.customers) })),
			meta: { query_ms: 0, cached: false },
		};
	}

	public async getTimeBetween(request: TimeBetweenRequest): Promise<TimeBetweenResult> {
		const gaps = members(request.filters, request.segment).flatMap((item) => {
			const gap = gapFor(item, request.segment);
			return gap === null ? [] : [gap];
		});
		let previous = 0;
		const bins = TIME_BINS.map(([label, limit]) => {
			const count = gaps.filter((gap) => gap >= previous && gap < limit).length;
			previous = limit;
			return { label, customers: count, pct: pct(count, gaps.length) };
		});
		return {
			customers: gaps.length,
			p25_secs: quantile(gaps, 0.25),
			median_secs: median(gaps),
			p75_secs: quantile(gaps, 0.75),
			bins,
			meta: { query_ms: 0, cached: false },
		};
	}

	public async getTimeline(customerId: string): Promise<CustomerTimeline> {
		const customer = customers().find((candidate) => candidate.id === customerId);
		if (!customer) throw new Error('Customer not found in the Subscription demo.');
		return {
			customer_id: customer.id,
			start_date: customer.start,
			attributes: labelledAttributes(customer),
			steps: customer.steps.map((step, index) => ({
				name: step.name,
				type: step.type,
				timestamp: toIso(step.ts),
				secs_since_previous: index === 0 ? null : step.ts - customer.steps[index - 1].ts,
			})),
			truncated: false,
		};
	}

	public async exportCustomers(request: JourneyExportRequest): Promise<Blob> {
		const header = [
			'customer_id',
			'start_date',
			'goal_date',
			...CUSTOMER_ATTRIBUTES.map((a) => a.label),
			'journey',
		];
		const lines = members(request.filters, segmentOf(request)).map((item) => {
			const customer = toCustomer(item);
			return [
				customer.customer_id,
				customer.start_date,
				customer.goal_date,
				...CUSTOMER_ATTRIBUTES.map((attribute) => item.customer.attributes[attribute.column] ?? null),
				customer.journey.join(PATH_SEPARATOR),
			]
				.map(csvCell)
				.join(',');
		});
		return new Blob([[header.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv' });
	}

	public getSavedViews(): Promise<SavedView[]> {
		return this.real().getSavedViews();
	}

	public saveView(name: string, state: SavedViewState): Promise<SavedView> {
		return this.real().saveView(name, state);
	}

	public deleteView(id: string): Promise<void> {
		return this.real().deleteView(id);
	}
}
